import * as React from "react";
import { Table, Thead, Tbody, Tr, Th, Td, chakra, Box } from "@chakra-ui/react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  getSortedRowModel,
  VisibilityState,
  RowSelectionState,
  Row,
  ColumnOrderState
} from "@tanstack/react-table";
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import { DomUtilsService } from '../services/DomUtilsService';

export type DataTableProps<Data extends object> = {
  data: Data[];
  emptyLabel: string,
  columns: ColumnDef<Data, any>[];
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => void;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => void;
  onRowClick?: (row: Data) => void;
  getRowId?: (row: Data) => string;
};

// {courses.length === 0 && <Text p='4' color='fg.muted' textAlign='center'> When there are course submission they will appear in this list</Text>}
export function DataTable<Data extends object>({
  data,
  emptyLabel,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  onRowClick,
  getRowId
}: DataTableProps<Data>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});

  // Use external row selection state if provided, otherwise use internal state
  const currentRowSelection = rowSelection ?? internalRowSelection;
  const handleRowSelectionChange = onRowSelectionChange ?? setInternalRowSelection;

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange,
    onColumnOrderChange,
    enableRowSelection,
    enableMultiRowSelection: false, // Enable single row selection only
    onRowSelectionChange: handleRowSelectionChange,
    getRowId,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      rowSelection: currentRowSelection
    }
  });

  const height = 'calc(100vh - 260px)'
  const rows = table.getRowModel().rows;

  const rowClickHandler = (evt: React.MouseEvent<HTMLTableRowElement>, row: Row<Data>) => {
    if (enableRowSelection)
      return row.getToggleSelectedHandler();
    
    if (onRowClick) {
      if (DomUtilsService.hasInteractiveAncestor(evt.target as HTMLElement, evt.currentTarget)) {
        return; // Ignore clicks on interactive elements or their children
      }
      onRowClick(row.original);
    }
  }

  return (
    <Box height={height} overflow="auto" borderWidth='1px' borderColor='gray.200'>
      <Table style={{ borderCollapse: 'separate', borderSpacing: 0, whiteSpace: 'break-spaces' }}>
        <Thead shadow='sm' position="sticky" top={0}>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                const meta: any = header.column.columnDef.meta;
                return (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    cursor={header.column.getCanSort() ? "pointer" : "default"}
                    isNumeric={meta?.isNumeric}
                    py={2} px={1}
                    minW={header.column.columnDef.minSize}
                    maxW={header.column.columnDef.maxSize}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    <chakra.span display='inline-block' pl="2">
                      {header.column.getIsSorted() ? (
                        header.column.getIsSorted() === "desc" ? (
                          <FiArrowDown />
                        ) : (
                          <FiArrowUp />
                        )
                      ) : null}
                    </chakra.span>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={row.id}
              bg={row.getIsSelected() ? 'brand.50' : 'transparent'}
              _hover={{ bg: row.getIsSelected() ? 'brand.100' : 'gray.50' }}
              cursor={enableRowSelection || onRowClick ? 'pointer' : 'default'}
              onClick={evt => rowClickHandler(evt, row)}
            >
              {row.getVisibleCells().map((cell) => {
                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                const meta: any = cell.column.columnDef.meta;
                return (
                  <Td py={1} key={cell.id} isNumeric={meta?.isNumeric} minW={cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
      {rows.length === 0 && <Box p='8' w='full' color='fg.muted' textAlign='center'>{emptyLabel}</Box>}
    </Box>
  );
}